<template>
	<core-layout-content medium>
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("create_product") }}</template>
		</core-layout-headline-and-actions>

		<!-- Language selector -->
		<div class="xs-4 mb4" v-if="$store.state.settings.allowedLocales">
			<div class="pr2 xs-12 fs3 lh125 fw7 pb1">{{ $t("language") }}</div>
			<core-select-locale
				class="xs-12"
				size="xsmall"
				v-model="selectedLocale"
			/>
		</div>

		<!-- Body -->
		<j-card class="xs-12 bg5 r2 p4">
			<!-- Name -->
			<core-form-input
				componentName="j-base-input-text"
				name="name"
				:label="$t('name')"
				v-validate="'required'"
				v-model="form.name"
				:validationErrors="errors"
			/>

			<!-- Plan -->
			<core-form-input
				class="mt6"
				componentName="j-base-input-text"
				name="plan"
				:label="$t('plan')"
				:placeholder="'really_great_product'"
				:description="$t('has_to_be_unique')"
				v-validate="{ required: true, alphanumeric_underscore: true }"
				v-model="form.plan"
				:validationErrors="errors"
			/>

			<!-- Version -->
			<core-form-input
				class="mt6"
				componentName="j-base-input-text"
				name="version"
				:label="$t('version')"
				:placeholder="'1'"
				:description="$t('has_to_be_unique')"
				v-validate="{ required: true, alphanumeric_underscore: true }"
				v-model="form.version"
				:validationErrors="errors"
			/>

			<!-- Type and insurer -->
			<div class="flex wrap xs-12 mt6">
				<div class="xs-12 md-6 md-pr2 mb6 md-mb0">
					<core-form-input
						componentName="core-select-insurance-type"
						name="type"
						:label="$t('type')"
						v-validate="{ required: true }"
						v-model="form.type"
						:validationErrors="errors"
					/>
				</div>
				<div class="xs-12 md-6 md-pl2">
					<core-form-input
						componentName="core-select-insurer"
						name="insurer"
						:label="$t('insurer')"
						v-validate="{ required: true }"
						v-model="form.insurer"
						:validationErrors="errors"
					/>
				</div>
			</div>

			<!-- Buttons -->
			<div class="mt6">
				<j-button :isLoading="isLoading" @click="handleSubmit">{{
					$t("create_product")
				}}</j-button>
			</div>
		</j-card>
	</core-layout-content>
</template>

<script>
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import JCard from "@/components/JCard/"
import JButton from "@/components/JButton/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent.vue"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions.vue"
import CoreSelectLocale from "@/modules/core/components/CoreSelectLocale"

import { i18n } from "@/locales/setup.js"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		CoreFormInput,
		JCard,
		JButton,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CoreSelectLocale
	},

	data: () => ({
		isLoading: false,

		selectedLocale: i18n.locale,

		form: {
			name: null,
			type: null,
			insurer: null,
			plan: null,
			version: null
		}
	}),

	methods: {
		async handleSubmit() {
			// Validate form
			const isValid = await this.$validator.validateAll()
			if (!isValid) return

			//---------------------------------------
			// 1: Create Product
			//---------------------------------------
			const { plan, type, version, insurer, name } = this.form
			this.isLoading = true

			const query1 = gql`
				mutation createProduct($product: createProductInput!) {
					createProduct(product: $product) {
						productId {
							plan
							type
							version
						}
					}
				}
			`
			const variables1 = {
				product: {
					productId: {
						plan: plan,
						type: type,
						version: version
					},
					insurerId: insurer,
					// NOTE: we are adding "product" tag to newly created product
					tags: [
						{
							type: "product"
						}
					]
				}
			}
			const res = await api({ query: query1, variables: variables1 })

			this.isLoading = false

			//---------------------------------------
			// 2: Localize Product - name
			//---------------------------------------
			const id = res.data.createProduct.productId || null
			if (!id) return

			const l10n = {
				locale: this.selectedLocale,
				key: `products-${plan + "|" + version + "|" + type}-name`,
				value: name
			}

			const query2 = gql`
				mutation localizeProduct($l10n: upsertL10nInput) {
					upsertL10n(l10n: $l10n) {
						errors
						status
					}
				}
			`

			const variables2 = {
				l10n
			}

			this.isLoading = true
			const res2 = await api({ query: query2, variables: variables2 })
			this.isLoading = false

			if (res2?.data?.upsertL10n?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res2?.data?.upsertL10n?.errors[0] || this.$t("errors.general")
					}
				})
			}

			// Redirect to product detail of created product
			this.$router.push({
				name: "ProductDetail",
				params: { plan, type, version }
			})
		}
	}
}
</script>
